import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCarbonProjects } from '@/hooks/useCarbonProjects';
import { useTransactions } from '@/hooks/useTransactions';
import { useNotifications } from '@/hooks/useNotifications';
import { RFPsTab } from './RFPsTab';
import { 
  TreePine, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  Plus,
  Gavel,
  Target,
  FileText,
  BarChart3,
  Settings,
  Bell,
  CheckCircle2,
  Clock,
  Calendar,
  MapPin,
  Eye,
  ExternalLink,
  Briefcase
} from 'lucide-react';

interface ProjectDeveloperDashboardProps {
  user: User;
  profile: any;
}

// Mock data for ICX-specific metrics
const mockMarketData = {
  avgPrice: 850, // INR per credit
  todayVolume: 12450,
  monthlyRevenue: 2.45, // crores
  activeListings: 3,
  pendingActions: [
    { id: 1, type: 'auction_approval', message: 'Solar Maharashtra auction pending admin approval', urgent: true },
    { id: 2, type: 'kyc_update', message: 'KYC documentation requires renewal', urgent: false },
    { id: 3, type: 'project_verification', message: 'Wind Farm Tamil Nadu ready for verification', urgent: true }
  ]
};

export const ProjectDeveloperDashboard: React.FC<ProjectDeveloperDashboardProps> = ({ user, profile }) => {
  const { projects, loading } = useCarbonProjects(user.id);
  const { transactions, getUserTransactions } = useTransactions();
  const { notifications, unreadCount } = useNotifications();
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();
  
  // Calculate ICX-specific metrics
  const totalCreditsRegistered = projects.reduce((sum, p) => sum + Number(p.credits_issued || 0), 0);
  const totalCreditsSold = transactions.reduce((sum, t) => sum + Number(t.quantity || 0), 0);
  const availableInventory = totalCreditsRegistered - totalCreditsSold;
  const totalRevenue = transactions.reduce((sum, t) => sum + Number(t.total_amount || 0), 0);
  const avgSalePrice = totalCreditsSold > 0 ? totalRevenue / totalCreditsSold : 0;

  useEffect(() => {
    if (user.id) {
      getUserTransactions(user.id);
    }
  }, [user.id]);

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) { // 1 crore
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) { // 1 lakh
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'verified': { icon: CheckCircle2, color: 'bg-green-100 text-green-800', label: 'Verified' },
      'under_review': { icon: Clock, color: 'bg-orange-100 text-orange-800', label: 'Under Review' },
      'pending': { icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      'active': { icon: TrendingUp, color: 'bg-blue-100 text-blue-800', label: 'Active Sale' },
      'draft': { icon: FileText, color: 'bg-gray-100 text-gray-800', label: 'Draft' }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.draft;
    const IconComponent = config.icon;
    
    return (
      <Badge className={config.color}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/10 rounded-lg">
            <TreePine className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Project Developer Portal</h1>
            <p className="text-muted-foreground">
              India Carbon Exchange (ICX) - Carbon Credit Trading Scheme (CCTS)
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm">
            <Bell className="w-4 h-4 mr-2" />
            Notifications ({unreadCount})
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Dashboard Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Portfolio Summary */}
        <Card className="border-l-4 border-l-primary">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Portfolio Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Registered:</span>
                <span className="font-semibold">{totalCreditsRegistered.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Sold:</span>
                <span className="font-semibold text-green-600">{totalCreditsSold.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>Available:</span>
                <span className="text-primary">{availableInventory.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Snapshot */}
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Revenue Snapshot</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(totalRevenue)}
              </div>
              <div className="text-sm text-muted-foreground">Total Revenue</div>
              <div className="flex justify-between text-sm">
                <span>Avg. Price:</span>
                <span className="font-semibold">₹{Math.round(avgSalePrice)}/credit</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Listings */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Listings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-blue-600">
                {mockMarketData.activeListings}
              </div>
              <div className="text-sm text-muted-foreground">Ongoing Sales</div>
              <div className="flex items-center gap-1 text-sm">
                <Gavel className="w-3 h-3" />
                <span>2 Auctions, 1 ICO</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Actions */}
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-orange-600">
                {mockMarketData.pendingActions.length}
              </div>
              <div className="text-sm text-muted-foreground">Requires Attention</div>
              <div className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-3 h-3" />
                <span>{mockMarketData.pendingActions.filter(a => a.urgent).length} Urgent</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Actions Alert */}
      {mockMarketData.pendingActions.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockMarketData.pendingActions.map((action) => (
                <div key={action.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-3">
                    {action.urgent && <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
                    <span className={action.urgent ? 'font-semibold' : ''}>{action.message}</span>
                  </div>
                  <Button size="sm" variant={action.urgent ? 'default' : 'outline'}>
                    Review
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Dashboard</TabsTrigger>
          <TabsTrigger value="projects" onClick={() => navigate('/project-projects')}>My Projects</TabsTrigger>
          <TabsTrigger value="registration" onClick={() => navigate('/projects/new')}>Project Registration</TabsTrigger>
          <TabsTrigger value="sales" onClick={() => navigate('/project-sales')}>Sales & Revenue</TabsTrigger>
          <TabsTrigger value="verification" onClick={() => navigate('/project-verification')}>Verification</TabsTrigger>
          <TabsTrigger value="analytics" onClick={() => navigate('/project-analytics')}>Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Projects */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Recent Projects</span>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Project
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {projects.length === 0 ? (
                  <div className="text-center py-8">
                    <TreePine className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-semibold mb-2">No Projects Registered</h3>
                    <p className="text-muted-foreground mb-4">
                      Start by adding your first carbon credit project to the ICX registry
                    </p>
                    <Button onClick={() => navigate('/projects/new')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Register Project
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projects.slice(0, 4).map((project) => (
                      <div key={project.registry_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                        <div className="flex items-center gap-3">
                          <TreePine className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-medium">{project.name}</p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {project.location}
                              </span>
                              <span>{Number(project.credits_issued).toLocaleString()} tCO₂e</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(project.status)}
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sales Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Sales Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-green-800">This Month</span>
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-green-700">{formatCurrency(mockMarketData.monthlyRevenue * 10000000)}</div>
                    <div className="text-sm text-green-600">+18% from last month</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 border rounded-lg">
                      <div className="text-sm text-muted-foreground">Credits Sold</div>
                      <div className="text-lg font-bold">{totalCreditsSold.toLocaleString()}</div>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <div className="text-sm text-muted-foreground">Avg. Price</div>
                      <div className="text-lg font-bold">₹{Math.round(avgSalePrice)}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="projects" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Project Management</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => navigate('/project-projects')}>
                    <Eye className="w-4 h-4 mr-2" />
                    View All Projects
                  </Button>
                  <Button onClick={() => navigate('/projects/new')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Register New Project
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Enhanced Project Management</h3>
                <p className="text-muted-foreground mb-4">
                  Access comprehensive project tracking, verification management, and registration tools
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <Button variant="outline" onClick={() => navigate('/project-projects')}>
                    <Briefcase className="w-4 h-4 mr-2" />
                    My Projects
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/project-verification')}>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Verification
                  </Button>
                  <Button onClick={() => navigate('/projects/new')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Register New
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sales" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Gavel className="w-5 h-5" />
                  Credit Sales & Revenue
                </span>
                <Button variant="outline" onClick={() => navigate('/project-sales')}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Full Dashboard
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="p-6 hover:bg-muted/50 cursor-pointer border-2 border-dashed" onClick={() => navigate('/project-sales')}>
                    <Gavel className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                    <h3 className="text-lg font-semibold mb-2">Create Auction</h3>
                    <p className="text-sm text-muted-foreground">Set up competitive bidding for your credits</p>
                  </Card>
                  <Card className="p-6 hover:bg-muted/50 cursor-pointer border-2 border-dashed" onClick={() => navigate('/project-sales')}>
                    <DollarSign className="w-12 h-12 mx-auto mb-4 text-green-600" />
                    <h3 className="text-lg font-semibold mb-2">Revenue Analytics</h3>
                    <p className="text-sm text-muted-foreground">Track sales performance and revenue</p>
                  </Card>
                </div>
                <Button className="mt-6" onClick={() => navigate('/project-sales')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Access Sales Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="registration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Project Registration
                </span>
                <Button onClick={() => navigate('/projects/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Register New Project
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Register Your Carbon Project</h3>
                <p className="text-muted-foreground mb-6">
                  Submit your carbon credit project for verification and inclusion in the ICX registry
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Project Verification</h4>
                    <p className="text-xs text-muted-foreground">Comprehensive validation process</p>
                  </div>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Market Access</h4>
                    <p className="text-xs text-muted-foreground">Direct access to carbon markets</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/projects/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Start Registration
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verification" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Project Verification
                </span>
                <Button variant="outline" onClick={() => navigate('/project-verification')}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Verification Dashboard
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Project Verification Management</h3>
                <p className="text-muted-foreground mb-6">
                  Track verification status, submit documentation, and manage compliance requirements
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <Clock className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Pending Review</h4>
                    <p className="text-xs text-muted-foreground">Awaiting verification</p>
                  </div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Verified</h4>
                    <p className="text-xs text-muted-foreground">Ready for trading</p>
                  </div>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Documentation</h4>
                    <p className="text-xs text-muted-foreground">Manage compliance docs</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/project-verification')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Verification Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rfps" className="space-y-6">
          <RFPsTab />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Performance Analytics
                </span>
                <Button variant="outline" onClick={() => navigate('/project-analytics')}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Full Analytics
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Comprehensive Analytics Dashboard</h3>
                <p className="text-muted-foreground mb-6">
                  Access detailed performance metrics, revenue trends, market analysis, and compliance reporting
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Revenue Trends</h4>
                    <p className="text-xs text-muted-foreground">Monthly & quarterly analysis</p>
                  </div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Market Performance</h4>
                    <p className="text-xs text-muted-foreground">Vs industry benchmarks</p>
                  </div>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <CheckCircle2 className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <h4 className="font-semibold">Compliance Score</h4>
                    <p className="text-xs text-muted-foreground">Standards & verification</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/project-analytics')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Analytics Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};